import {
  DEFAULT_LIGHT_BACKGROUND_COLOR,
  DEFAULT_LIGHT_COLOR,
  DEFAULT_LIGHT_ERROR_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_LIGHT_SUCCESS_COLOR,
} from '@/constants';
import {
  ALIGNMENT_OPTIONS,
  BORDER_RADIUS_OPTIONS,
  CAPTION_LAYOUT_OPTIONS,
  COLOR_SCHEME_OPTIONS,
  dateFormatEnum,
  DAY_OF_WEEK_OPTIONS,
  DISPLAY_TYPE_OPTIONS,
  LAYOUT_OPTIONS,
  LOCALE_OPTIONS,
  LOCATION_LAYOUT_OPTIONS,
  NAVIGATION_LAYOUT_OPTIONS,
  SIZE_OPTIONS,
  timeFormatEnum,
} from '@/constants/enum';
import {
  ColorError,
  IDisableWidgetAppearance,
  IMainWidgetSettings,
  IWidgetColor,
  IWidgetColorValidation,
  IWidgetDeliveryText,
  IWidgetPickupText,
  IWidgetShippingText,
  IWidgetState,
} from '@/types/components/widget';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { ITextDetail } from '@/types/components/translation';

export const defaultErrorValue: ColorError = {
  error: '',
  isShow: false,
};

export const widgetSettingInitial: IMainWidgetSettings = {
  id: -1,
  shop: '',
  timeZone: '',
  sortMethods: [],
  defaultMethod: '',
  widgetPosition: {
    displayOnPage: [],
    cartPageSelector: '',
    cartPagePosition: '',
    cartDrawerSelector: '',
    cartDrawerPosition: '',
    productPageSelector: '',
    productPagePosition: '',
  },
  widgetMethods: {
    layout: LAYOUT_OPTIONS.HORIZONTAL,
    displayType: DISPLAY_TYPE_OPTIONS.ICON_TEXT,
    borderRadius: BORDER_RADIUS_OPTIONS.SMALL,
    locationLayout: LOCATION_LAYOUT_OPTIONS.HORIZONTAL,
    hideHeader: false,
    hideHeadingTitle: false,
    hideCheckIcon: true,
    datePickerAlignment: ALIGNMENT_OPTIONS.CENTER,
    hidePickupLocation: false,
  },
  customizations: {
    captionLayout: CAPTION_LAYOUT_OPTIONS.LABEL,
    showOutsideDays: false,
    showWeekNumber: false,
    showWeekDays: true,
    fixedWeeks: false,
    weekStartsOn: DAY_OF_WEEK_OPTIONS.MONDAY,
    rightToLeft: false,
    hideNavigation: false,
    startOnThisMonth: true,
    alwaysShowCalendar: true,
    disableNavigation: false,
    navigationLayout: NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT,
    locale: LOCALE_OPTIONS.EN_US,
    dateFormat: dateFormatEnum.DAY_MONTH_YEAR_WITH_WEEKDAYS,
    timeFormat: timeFormatEnum.TWELVE_HOURS_FORMAT,
  },
  colorScheme: COLOR_SCHEME_OPTIONS.LIGHT,
  headerTitle: '',
  additionalMessage: '',
  customMessage: '',
  additionalCss: '',
  sizes: {
    borderRadius: BORDER_RADIUS_OPTIONS.NONE,
    widgetSize: SIZE_OPTIONS.LG,
    headingFontSize: DEFAULT_FONT_SIZE,
    errorMessageFontSize: DEFAULT_FONT_SIZE,
    inputFontSize: DEFAULT_FONT_SIZE,
    inputTitleFontSize: DEFAULT_FONT_SIZE,
    labelFontSize: DEFAULT_FONT_SIZE,
    additionalMessageFontSize: DEFAULT_FONT_SIZE,
  },
};

export const widgetColorInitial: IWidgetColor = {
  id: 1,
  calendar: {
    calendarBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    calendarBorder: DEFAULT_LIGHT_COLOR,
    selectedDateColor: DEFAULT_LIGHT_BACKGROUND_COLOR,
    selectedDateBackgroundColor: DEFAULT_LIGHT_COLOR,
    availableDateColor: DEFAULT_LIGHT_COLOR,
    unavailableDateColor: DEFAULT_LIGHT_COLOR,
  },
  header: {
    headerTitleColor: DEFAULT_LIGHT_COLOR,
    buttonColor: DEFAULT_LIGHT_COLOR,
    buttonBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    buttonBorder: DEFAULT_LIGHT_COLOR,
    hoverButtonBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    hoverButtonColor: DEFAULT_LIGHT_COLOR,
    hoverButtonBorder: DEFAULT_LIGHT_COLOR,
    selectedButtonBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    selectedButtonColor: DEFAULT_LIGHT_COLOR,
    selectedButtonBorder: DEFAULT_LIGHT_COLOR,
    dateTitleColor: DEFAULT_LIGHT_COLOR,
    timeTitleColor: DEFAULT_LIGHT_COLOR,
    inputBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    inputBorder: DEFAULT_LIGHT_COLOR,
    inputTextDateColor: DEFAULT_LIGHT_COLOR,
    inputTextTimeColor: DEFAULT_LIGHT_COLOR,
  },
  body: {
    inputColor: DEFAULT_LIGHT_COLOR,
    inputBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    inputBorder: DEFAULT_LIGHT_COLOR,
    checkButtonColor: DEFAULT_LIGHT_COLOR,
    checkButtonBackground: DEFAULT_LIGHT_BACKGROUND_COLOR,
    checkButtonBorder: DEFAULT_LIGHT_COLOR,
    eligibleMessageColor: DEFAULT_LIGHT_SUCCESS_COLOR,
    requiredMessageColor: DEFAULT_LIGHT_COLOR,
    errorColor: DEFAULT_LIGHT_ERROR_COLOR,
    additionalNotesColor: DEFAULT_LIGHT_COLOR,
  },
};

const widgetColorErrorsInitial: IWidgetColorValidation = {
  calendar: {
    calendarBackground: defaultErrorValue,
    calendarBorder: defaultErrorValue,
    selectedDateColor: defaultErrorValue,
    selectedDateBackgroundColor: defaultErrorValue,
    availableDateColor: defaultErrorValue,
    unavailableDateColor: defaultErrorValue,
  },
  header: {
    headerTitleColor: defaultErrorValue,
    buttonColor: defaultErrorValue,
    buttonBackground: defaultErrorValue,
    buttonBorder: defaultErrorValue,
    hoverButtonBackground: defaultErrorValue,
    hoverButtonColor: defaultErrorValue,
    hoverButtonBorder: defaultErrorValue,
    selectedButtonBackground: defaultErrorValue,
    selectedButtonColor: defaultErrorValue,
    selectedButtonBorder: defaultErrorValue,
    dateTitleColor: defaultErrorValue,
    timeTitleColor: defaultErrorValue,
    inputBackground: defaultErrorValue,
    inputBorder: defaultErrorValue,
    inputTextDateColor: defaultErrorValue,
    inputTextTimeColor: defaultErrorValue,
  },
  body: {
    inputColor: defaultErrorValue,
    inputBackground: defaultErrorValue,
    inputBorder: defaultErrorValue,
    checkButtonColor: defaultErrorValue,
    checkButtonBackground: defaultErrorValue,
    checkButtonBorder: defaultErrorValue,
    eligibleMessageColor: defaultErrorValue,
    requiredMessageColor: defaultErrorValue,
    errorColor: defaultErrorValue,
    additionalNotesColor: defaultErrorValue,
  },
};

export const WIDGET_PICKUP_TEXT_INITIAL: IWidgetPickupText = {
  id: 0,
  labelAndButtons: {
    buttonLabel: 'Store pickup',
    dateTitle: 'Pickup date',
    captionDateTitle: 'Pick a date',
    instructions: 'Choose the nearest location for your pickup',
    timeTitle: '',
    captionTimeTitle: 'Pick a time',
  },
  isUseAdditionalNote: false,
  isShowMessageMinimum: false,
  requiredConditions: {
    requiredMessage: '',
  },
  errorMessages: {
    fontSize: DEFAULT_FONT_SIZE,
    eligibleForPickup: '',
  },
  additionalMessages: {
    minimumOrderValue: '',
    minimumOrderWeight: '',
  },
};

export const WIDGET_DELIVERY_TEXT_INITIAL: IWidgetDeliveryText = {
  id: 0,
  labelAndButtons: {
    buttonLabel: 'Local delivery',
    instructions: 'Enter your zip or postal code and tap check for local delivery',
    zipCodeLabel: 'Postal code',
    checkButton: 'Check',
    dateTitle: 'Local delivery date',
    timeTitle: '',
  },
  isUseAdditionalNote: false,
  isShowMessageMinimum: false,
  requiredConditions: {
    requiredMessage: '',
    eligibleNotice: 'Local delivery is available in your area',
  },
  errorMessages: {
    fontSize: DEFAULT_FONT_SIZE,
    notSupportedZone: '',
    eligibleForDelivery: '',
  },
  additionalMessages: {
    minimumOrderValue: '',
    minimumOrderWeight: '',
  },
};

export const WIDGET_SHIPPING_TEXT_INITIAL: IWidgetShippingText = {
  id: 0,
  labelAndButtons: {
    buttonLabel: 'Shipping',
    dateTitle: 'Shipping date',
    timeTitle: '',
  },
  isUseAdditionalNote: false,
  isShowMessageMinimum: false,
  requiredConditions: {
    requiredMessage: '',
  },
  errorMessages: {
    fontSize: DEFAULT_FONT_SIZE,
    eligibleForShipping: '',
  },
  additionalMessages: {
    minimumOrderValue: '',
    minimumOrderWeight: '',
  },
};

const widgetStateInitial: IWidgetState = {
  isSaving: false,
  isLoading: false,
  isChecking: false,
  isDisableAdditionalMessage: false,
};

const widgetTextDetailInitial: ITextDetail = {};

const widgetAppearanceDisableInitial: IDisableWidgetAppearance = {
  isDisableCustomStyle: false,
  isDisableAutoTranslate: false,
  isDisableAdditionalMessage: false,
};

const initialState = {
  widget: {
    widgetSettings: widgetSettingInitial,
    widgetState: widgetStateInitial,
    widgetColors: widgetColorInitial,
    widgetColorErrors: widgetColorErrorsInitial,
    widgetTextDefaultDetail: widgetTextDetailInitial,
  },
  widgetBackup: {
    widgetSettings: widgetSettingInitial,
    widgetState: widgetStateInitial,
    widgetColors: widgetColorInitial,
    widgetColorErrors: widgetColorErrorsInitial,
    widgetTextDefaultDetail: widgetTextDetailInitial,
  },
  widgetAppearanceDisable: widgetAppearanceDisableInitial,
};

const widgetSlice = createSlice({
  name: 'widget',
  initialState,
  reducers: {
    handleSaveWidgetSettings: (state, action: PayloadAction<Partial<IMainWidgetSettings>>) => {
      state.widget.widgetSettings = { ...state.widget.widgetSettings, ...action.payload };
    },
    handleSetWidgetState: (state, action: PayloadAction<Partial<IWidgetState>>) => {
      state.widget.widgetState = { ...state.widget.widgetState, ...action.payload };
    },
    handleSaveWidgetColors: (state, action: PayloadAction<Partial<IWidgetColor>>) => {
      state.widget.widgetColors = { ...state.widget.widgetColors, ...action.payload };
    },
    handleSetWidgetColorError: (state, action: PayloadAction<Partial<IWidgetColorValidation>>) => {
      state.widget.widgetColorErrors = { ...state.widget.widgetColorErrors, ...action.payload };
    },
    handleSaveWidgetTextDefaultDetail: (state, action: PayloadAction<Partial<ITextDetail>>) => {
      state.widget.widgetTextDefaultDetail = { ...state.widget.widgetTextDefaultDetail, ...action.payload };
    },
    handleSaveBackupWidgetSettings: (state, action: PayloadAction<Partial<IMainWidgetSettings>>) => {
      state.widgetBackup.widgetSettings = { ...state.widgetBackup.widgetSettings, ...action.payload };
    },
    handleSetBackupWidgetState: (state, action: PayloadAction<Partial<IWidgetState>>) => {
      state.widgetBackup.widgetState = { ...state.widgetBackup.widgetState, ...action.payload };
    },
    handleSaveBackupWidgetColors: (state, action: PayloadAction<Partial<IWidgetColor>>) => {
      state.widgetBackup.widgetColors = { ...state.widgetBackup.widgetColors, ...action.payload };
    },
    handleSaveBackupWidgetTextDefaultDetail: (state, action: PayloadAction<Partial<ITextDetail>>) => {
      state.widgetBackup.widgetTextDefaultDetail = { ...state.widgetBackup.widgetTextDefaultDetail, ...action.payload };
    },
    handleResetWidgetColorError: (state) => {
      state.widget.widgetColorErrors = widgetColorErrorsInitial;
    },
    handleResetWidgetState: (state) => {
      state.widget.widgetState = widgetStateInitial;
    },
    handleResetWidget: (state) => {
      state.widget = state.widgetBackup;
    },
    handleSetWidgetAppearanceDisable: (state, action: PayloadAction<Partial<IDisableWidgetAppearance>>) => {
      state.widgetAppearanceDisable = { ...state.widgetAppearanceDisable, ...action.payload };
    },
  },
});

export const widgetSettingSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widget.widgetSettings,
);
export const widgetStateSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widget.widgetState,
);
export const widgetColorSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widget.widgetColors,
);
export const widgetColorErrorsSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widget.widgetColorErrors,
);
export const widgetTextDefaultDetailSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widget.widgetTextDefaultDetail,
);

export const oldWidgetDataSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widgetBackup,
);
export const currentWidgetDataSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widget,
);

export const widgetAppearanceDisableSelector = createSelector(
  (state: RootState) => state.widget,
  (state) => state.widgetAppearanceDisable,
);

export const {
  handleSaveWidgetSettings,
  handleSetWidgetState,
  handleSaveWidgetColors,
  handleSetWidgetColorError,
  handleSaveBackupWidgetSettings,
  handleSetBackupWidgetState,
  handleSaveBackupWidgetColors,
  handleResetWidget,
  handleResetWidgetState,
  handleResetWidgetColorError,
  handleSaveWidgetTextDefaultDetail,
  handleSaveBackupWidgetTextDefaultDetail,
  handleSetWidgetAppearanceDisable,
} = widgetSlice.actions;

export default widgetSlice;
